import {
  ApiError,
  CertifyLicensingAgencyRequest,
  LicensingAgenciesService,
  LicensingAgency,
  UpdateLicensingAgencyRequest,
} from '@/@types/generated'
import { CreateLicensingAgencyRequest } from '@/@types/generated/models/CreateLicensingAgencyRequest'
import { useError } from '@/hooks/useError'
import React, { useMemo } from 'react'

export const LicensingAgencyNew = 'new'

interface LicensingAgencyContextProps {
  readonly loading: boolean
  readonly licensingAgency: LicensingAgency | null

  // Methods
  create(req: CreateLicensingAgencyRequest): Promise<string>
  update(req: UpdateLicensingAgencyRequest): Promise<void>
  certify(req: CertifyLicensingAgencyRequest): Promise<void>
}

const LicensingAgencyContext = React.createContext<LicensingAgencyContextProps>(
  undefined!
)

interface LicensingAgencyProviderProps {
  agencyId: string // can be "new"
  onNotFound?: () => void
}

export function LicensingAgencyProvider({
  agencyId,
  children,
  onNotFound,
}: React.PropsWithChildren<LicensingAgencyProviderProps>) {
  // Hooks
  const { showError } = useError()

  // State
  const [loading, setLoading] = React.useState(0)
  const [licensingAgency, setLicensingAgency] =
    React.useState<LicensingAgency | null>(null)

  // Load the licensing agency for the current user from the API
  const get = React.useCallback(
    async (background?: boolean) => {
      if (!agencyId || agencyId === LicensingAgencyNew)
        return setLicensingAgency(null)

      try {
        if (!background) setLoading((prev) => prev + 1)
        const licensingAgency = await LicensingAgenciesService.get(agencyId)
        setLicensingAgency(licensingAgency)
      } catch (error) {
        // Catch 404, means no licensing agency exists
        if (error instanceof ApiError && error.status === 404) {
          setLicensingAgency(null)
          onNotFound?.()
          return
        }

        // Other errors
        showError(error, 'Failed to load licensing agency')
        throw error
      } finally {
        if (!background) setLoading((prev) => prev - 1)
      }
    },
    [onNotFound, agencyId, showError]
  )

  // Register the licensing agency information, returning and setting the registration
  const create = React.useCallback(
    async (req: CreateLicensingAgencyRequest): Promise<string> => {
      // Licensing agency already exists, do not allow re-registration
      if (licensingAgency)
        throw new Error('Licensing agency is already registered')

      try {
        const { licensingAgencyId } = await LicensingAgenciesService.create(req)
        return licensingAgencyId
      } catch (error) {
        showError(error, 'Failed to register licensing agency')
        throw error
      }
    },
    [licensingAgency, showError]
  )

  // Update and reload
  const update = React.useCallback(
    async (req: UpdateLicensingAgencyRequest): Promise<void> => {
      try {
        await LicensingAgenciesService.update(agencyId, req)
        await get(true) // reload (silently)
      } catch (error) {
        showError(error, 'Failed to update licensing agency')
        throw error
      }
    },
    [agencyId, get, showError]
  )

  // Certify the agency application
  const certify = React.useCallback(
    async (req: CertifyLicensingAgencyRequest): Promise<void> => {
      try {
        await LicensingAgenciesService.certify(agencyId, req)
        await get(true) // reload (silently)
      } catch (error) {
        showError(error, 'Failed to certify licensing agency')
        throw error
      }
    },
    [agencyId, get, showError]
  )

  // Load licensing agency on mount, if ID is provided
  React.useEffect(() => {
    get()
  }, [get])

  const isLoading = useMemo(() => loading > 0, [loading])
  return (
    <LicensingAgencyContext.Provider
      value={{
        loading: isLoading,
        licensingAgency,

        // Methods
        create,
        update,
        certify,
      }}
    >
      {children}
    </LicensingAgencyContext.Provider>
  )
}

export function useLicensingAgency() {
  const context = React.useContext(LicensingAgencyContext)
  if (context === undefined)
    throw new Error(
      'useLicensingAgency must be used within a LicensingAgencyProvider'
    )

  return context
}
